import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import type { BulkAssignBody, BulkAssignResult } from "@/lib/api/chatApi";
import type { FilaAgente } from "@/lib/permisosPorAgente";
import { parseEmails } from "@/lib/userTable";
import { agentChipClass } from "@/lib/agentColors";

interface Props {
  filas: FilaAgente[];
  /** Agentes desplegados. Viven en AdminPage: el estado de la pestaña no se pierde al salir y volver. */
  abiertos: ReadonlySet<string>;
  onAlternar: (graphId: string) => void;
  bulkAssign: (body: BulkAssignBody) => Promise<BulkAssignResult>;
  /** Recarga la lista de usuarios tras un cambio, para que las dos vistas cuenten lo mismo. */
  onCambio: () => Promise<void>;
}

/**
 * Vista "por agente" de la pestaña de permisos: acordeon agente -> usuarios.
 *
 * Toda escritura va por `POST /admin/users/bulk` y NO por `PUT /users/{email}`:
 * el PUT reescribe el array entero de agentes del usuario, asi que quitarle uno
 * desde aqui le pisaria el cambio a otro admin que acabara de tocarlo. `remove`
 * quita SOLO ese agente, atomico en SQL.
 */
export default function PermisosPorAgente({ filas, abiertos, onAlternar, bulkAssign, onCambio }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [anadirA, setAnadirA] = useState<string | null>(null);
  const [emails, setEmails] = useState("");
  const [invalidos, setInvalidos] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);

  const etiqueta = (id: string) => t(`agents.${id}.label`, id);

  const quitar = async (email: string, graphId: string) => {
    if (!confirm(t("admin.permissions.porAgente.quitarConfirm", { email, agente: etiqueta(graphId) }))) return;
    setError(null);
    try {
      await bulkAssign({ emails: [email], agents: [graphId], op: "remove", validate: false });
    } catch {
      setError(t("admin.permissions.porAgente.error"));
      return;
    }
    // El guardado ya tuvo exito: un fallo aqui es solo de RECARGA, no de guardado.
    try {
      await onCambio();
    } catch {
      setError(t("admin.permissions.porAgente.recargaFallida"));
    }
  };

  const abrirAnadir = (graphId: string) => {
    setAnadirA(graphId);
    setEmails("");
    setInvalidos([]);
    setError(null);
  };

  const cerrarAnadir = () => {
    setAnadirA(null);
    setError(null);
  };

  const anadir = async () => {
    if (!anadirA) return;
    setGuardando(true);
    setError(null);
    let res: BulkAssignResult;
    try {
      // validate=true es todo-o-nada: con un solo email inexistente no se aplica nada.
      res = await bulkAssign({ emails: parseEmails(emails), agents: [anadirA], op: "add", validate: true });
    } catch {
      setError(t("admin.permissions.porAgente.error"));
      setGuardando(false);
      return;
    }
    if (res.invalid.length > 0) {
      setInvalidos(res.invalid);
      setGuardando(false);
      return;
    }
    // El guardado ya tuvo exito: el modal se cierra igual, y un fallo de
    // recarga se avisa FUERA de el (no de guardado).
    setAnadirA(null);
    setGuardando(false);
    try {
      await onCambio();
    } catch {
      setError(t("admin.permissions.porAgente.recargaFallida"));
    }
  };

  const alerta = error && (
    <p role="alert" className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
      {error}
    </p>
  );

  return (
    <div>
      <p className="mb-3 text-xs text-[var(--muted-foreground)]">{t("admin.permissions.porAgente.avisoAdmins")}</p>
      {!anadirA && alerta}

      {filas.length === 0 ? (
        <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
          {t("admin.permissions.porAgente.sinResultados")}
        </p>
      ) : (
        <ul className="overflow-hidden rounded-lg border border-[var(--border)]">
          {filas.map((f) => {
            const abierto = abiertos.has(f.graphId) || f.casaPorEmail;
            const panel = `permisos-agente-${f.graphId}`;
            return (
              <li key={f.graphId} className="border-b border-[var(--border)] last:border-0">
                <div className="flex items-center gap-2 px-4 py-2">
                  <button
                    type="button"
                    aria-expanded={abierto}
                    aria-controls={panel}
                    // Si esta abierta por casar un email (busqueda), pulsar no
                    // cambia nada visible: alternaria SOLO el `abiertos` oculto,
                    // que se veria al cambiar la busqueda sin que el admin lo
                    // pidiera.
                    onClick={() => { if (!f.casaPorEmail) onAlternar(f.graphId); }}
                    className="flex flex-1 items-center gap-2 text-left text-sm"
                  >
                    {abierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className={`rounded-full px-2 py-0.5 text-xs ${agentChipClass(f.graphId)}`}>
                      {etiqueta(f.graphId)}
                    </span>
                    {f.hitl && (
                      <span className="rounded-full border border-amber-300 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-700 dark:text-amber-300">
                        {t("admin.modelos.tieneHitl")}
                      </span>
                    )}
                    {!f.registrado && (
                      <span title={t("admin.permissions.porAgente.noRegistradoAyuda")} className="flex items-center gap-1 text-xs text-amber-600">
                        <AlertTriangle size={12} />
                        {t("admin.permissions.porAgente.noRegistrado")}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-[var(--muted-foreground)]">
                      {t("admin.permissions.porAgente.usuarios", { count: f.total })}
                    </span>
                  </button>
                  {f.registrado && (
                    <button
                      type="button"
                      onClick={() => abrirAnadir(f.graphId)}
                      aria-label={t("admin.permissions.porAgente.anadirAria", { agente: etiqueta(f.graphId) })}
                      className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
                    >
                      <Plus size={12} />
                      {t("admin.permissions.porAgente.anadir")}
                    </button>
                  )}
                </div>

                {abierto && (
                  <div id={panel} className="px-4 pb-3 pl-10">
                    {!f.registrado && (
                      <p className="mb-2 text-xs text-amber-600">
                        {t("admin.permissions.porAgente.noRegistradoAyuda")}
                      </p>
                    )}
                    {f.emails.length === 0 ? (
                      <p className="text-xs text-[var(--muted-foreground)]">{t("admin.permissions.porAgente.sinUsuarios")}</p>
                    ) : (
                      <ul className="space-y-1">
                        {f.emails.map((email) => (
                          <li key={email} className="flex items-center justify-between text-sm">
                            <span>{email}</span>
                            {f.registrado && (
                              <button
                                type="button"
                                onClick={() => quitar(email, f.graphId)}
                                aria-label={t("admin.permissions.porAgente.quitarAria", { email })}
                                title={t("admin.permissions.porAgente.quitar")}
                                className="rounded p-1 hover:bg-[var(--muted)]"
                              >
                                <X size={14} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {anadirA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-anadir-agente"
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-[var(--border)] bg-[var(--background)] p-6 shadow-xl"
          >
            <h3 id="titulo-anadir-agente" className="mb-4 text-lg font-medium">
              {t("admin.permissions.porAgente.anadirTitulo", { agente: etiqueta(anadirA) })}
            </h3>
            <label htmlFor="emails-anadir-agente" className="mb-1 block text-sm font-medium">
              {t("admin.permissions.bulk.emailsLabel")}
            </label>
            <textarea
              id="emails-anadir-agente"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder={t("admin.permissions.bulk.emailsPlaceholder")}
              rows={5}
              className="mb-4 w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
            />
            {invalidos.length > 0 && (
              <div className="mb-4 rounded-lg border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-sm">
                <p className="font-medium text-amber-600">{t("admin.permissions.bulk.invalidTitle")}</p>
                <ul className="mt-1 list-inside list-disc text-xs text-[var(--muted-foreground)]">
                  {invalidos.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            )}
            {alerta}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cerrarAnadir}
                disabled={guardando}
                className="rounded-lg border border-[var(--border)] px-4 py-1.5 text-sm hover:bg-[var(--muted)] disabled:opacity-50"
              >
                {t("admin.permissions.bulk.close")}
              </button>
              <button
                type="button"
                onClick={anadir}
                disabled={guardando || parseEmails(emails).length === 0}
                className="rounded-lg bg-[var(--foreground)] px-4 py-1.5 text-sm text-[var(--background)] hover:opacity-90 disabled:opacity-50"
              >
                {t("admin.permissions.bulk.assign")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
